/**
 * Pagination footer for the website audit data table.
 *
 * Left:   "Showing X-Y of Z vehicles"
 * Middle: Page size dropdown (25 / 50 / 100 / 200)
 * Right:  Prev button, "Page N of M", Next button
 *
 * Mirrors PaginationFooter.tsx — uses useWebsiteAudit instead of useAudit.
 */
import React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"

import { useWebsiteAudit } from "../../context/WebsiteAuditContext"
import { THEME } from "../../config/theme"

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200]

export default function WebsitePaginationFooter() {
  const {
    page,
    setPage,
    size,
    setSize,
    totalMatching,
    vehiclesLoading,
  } = useWebsiteAudit()

  if (totalMatching === 0) return null

  const totalPages = Math.max(1, Math.ceil(totalMatching / size))
  const startItem = (page - 1) * size + 1
  const endItem = Math.min(page * size, totalMatching)

  const isFirstPage = page <= 1
  const isLastPage = page >= totalPages

  const handleSizeChange = (newSize: number) => {
    setSize(newSize)
    setPage(1)
  }

  const containerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: THEME.spacing.md,
    padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
    fontSize: 13,
    color: THEME.colors.neutral.light.textPrimary,
    backgroundColor: THEME.colors.neutral.light.background,
    border: `1px solid ${THEME.colors.silver}`,
    borderRadius: THEME.borderRadius.md,
    fontFamily: THEME.fonts.primary,
    flexWrap: "wrap",
    gap: THEME.spacing.sm,
  }

  const groupStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: THEME.spacing.sm,
  }

  const buttonStyle = (disabled: boolean): React.CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
    fontSize: 13,
    fontWeight: 500,
    borderRadius: THEME.borderRadius.md,
    border: `1px solid ${THEME.colors.silver}`,
    backgroundColor: THEME.colors.neutral.light.surface,
    color: disabled
      ? THEME.colors.neutral.light.textSecondary
      : THEME.colors.neutral.light.textPrimary,
    opacity: disabled ? 0.6 : 1,
    cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: THEME.fonts.primary,
    userSelect: "none",
    minWidth: 70,
    justifyContent: "center",
    transition: "background-color 0.15s, border-color 0.15s",
  })

  const selectStyle: React.CSSProperties = {
    padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
    fontSize: 13,
    borderRadius: THEME.borderRadius.md,
    border: `1px solid ${THEME.colors.silver}`,
    backgroundColor: THEME.colors.neutral.light.surface,
    color: THEME.colors.neutral.light.textPrimary,
    fontFamily: THEME.fonts.primary,
    cursor: "pointer",
  }

  return (
    <div style={containerStyle}>
      <div style={{ color: THEME.colors.neutral.light.textSecondary }}>
        Showing{" "}
        <strong style={{ color: THEME.colors.neutral.light.textPrimary }}>
          {startItem.toLocaleString()}&ndash;{endItem.toLocaleString()}
        </strong>{" "}
        of{" "}
        <strong style={{ color: THEME.colors.neutral.light.textPrimary }}>
          {totalMatching.toLocaleString()}
        </strong>{" "}
        vehicles
      </div>

      <div style={groupStyle}>
        <label
          htmlFor="website-page-size"
          style={{ color: THEME.colors.neutral.light.textSecondary }}
        >
          Rows per page:
        </label>
        <select
          id="website-page-size"
          value={size}
          onChange={(e) => handleSizeChange(Number(e.target.value))}
          style={selectStyle}
          disabled={vehiclesLoading}
        >
          {PAGE_SIZE_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>

      <div style={groupStyle}>
        <button
          type="button"
          onClick={() => setPage(page - 1)}
          disabled={isFirstPage || vehiclesLoading}
          style={buttonStyle(isFirstPage || vehiclesLoading)}
        >
          <ChevronLeft size={14} strokeWidth={2} />
          Prev
        </button>
        <div
          style={{
            color: THEME.colors.neutral.light.textSecondary,
            minWidth: 100,
            textAlign: "center",
          }}
        >
          Page{" "}
          <strong style={{ color: THEME.colors.neutral.light.textPrimary }}>
            {page}
          </strong>{" "}
          of{" "}
          <strong style={{ color: THEME.colors.neutral.light.textPrimary }}>
            {totalPages.toLocaleString()}
          </strong>
        </div>
        <button
          type="button"
          onClick={() => setPage(page + 1)}
          disabled={isLastPage || vehiclesLoading}
          style={buttonStyle(isLastPage || vehiclesLoading)}
        >
          Next
          <ChevronRight size={14} strokeWidth={2} />
        </button>
      </div>
    </div>
  )
}
